import Gun from "gun";

import { LOCAL_ACK_FLAG } from "./gunAck";
import { GUN_DB_NAME } from "./gunConfig";

/**
 * ------------------------------
 * STABLE INDEXEDDB ADAPTER (v2)
 * ------------------------------
 * A custom, lightweight GunDB plugin that uses IndexedDB directly.
 * Bypasses the unstable RAD/Radix engine entirely.
 */

/**
 * Guard Gun's Radix.map against the malformed radix trees Gun's own internals
 * occasionally hand it (a function whose `$` is missing). This is Gun-trap
 * knowledge, so it lives in the layer — it used to sit in the app's
 * `main.tsx`, where a project lifting this folder would silently lose it and
 * only find out when Radix.map threw at runtime.
 */
if (typeof window !== 'undefined') {
  const Radix = (window as any).Radix || (Gun as any).Radix;
  if (Radix && Radix.map) {
    const originMap = Radix.map;
    Radix.map = function rap(this: any, radix: any, cb: any, opt: any, pre: any) {
      const t = typeof radix === 'function' ? radix.$ || {} : radix;
      if (!t || typeof t !== 'object') return;
      return originMap.apply(this, [radix, cb, opt, pre]);
    };
  }
}

const DB_NAME = GUN_DB_NAME;
const STORE_NAME = "graph";

let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Set only when IndexedDB is structurally absent (non-browser test env, or an
 * environment that does not expose the API at all). The adapter then stops
 * trying and, crucially, stops reporting anything to Gun: local caching is an
 * optimisation, so its absence must never turn into a failed write ack.
 *
 * A *failed open* is deliberately not this. Blocked storage, storage pressure
 * and a concurrent version upgrade in another tab are all transient, and
 * latching them permanently silently degraded the app to all-network reads for
 * the rest of the session — the exact behaviour local caching exists to avoid.
 * Transient failures clear `dbPromise` instead, so the next operation retries.
 */
let idbUnavailable = false;

/** Backoff so a genuinely broken store does not retry on every message. */
let retryAfter = 0;
const RETRY_BACKOFF_MS = 5_000;

/**
 * Set while the store is being deleted out from under a live Gun instance.
 *
 * Deleting the database is not something Gun knows about: the instance keeps
 * serving reads and writes throughout, so the adapter reopened the database the
 * deleter had just closed (blocking the delete, which then retried, closing it
 * again) and the incoming account's freshly cached nodes were written into a
 * store about to be destroyed. Suspending is what makes the wipe atomic with
 * respect to the graph.
 *
 * Nothing is acknowledged while suspended: a local ack means "durable on this
 * device", and during the wipe it is not. Writes fall back to the relay's ack,
 * or fail and are retried by gunPutWithRetry once the store is back.
 */
let suspended = false;

/**
 * Run `fn` with the local store suspended and this connection closed, then
 * resume. Used by the account-switch wipe — see {@link suspended}.
 */
export async function withLocalStoreSuspended<T>(fn: () => Promise<T>): Promise<T> {
  suspended = true;
  try {
    closeDB();
    return await fn();
  } finally {
    suspended = false;
    // The wipe closed our handle repeatedly; do not let a backoff armed during
    // it keep the store dark after it is safe to reopen.
    retryAfter = 0;
  }
}

function getDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  if (typeof indexedDB === "undefined") {
    idbUnavailable = true;
    return Promise.reject(new Error("indexedDB unavailable"));
  }
  if (Date.now() < retryAfter) {
    return Promise.reject(new Error("indexedDB retry backoff"));
  }

  // `cached` is what lands in `dbPromise`, and it is what every "is this still
  // the current attempt?" check below must compare against. Comparing against
  // the raw promise instead is silently always false — `p.catch()` returns a
  // NEW promise — which made all three guards here dead code: a transient open
  // failure left the rejected promise cached forever (so `getDB` replayed the
  // same rejection for the rest of the session instead of retrying past the
  // backoff), and a connection closed by another tab's version change stayed
  // cached as a dead handle. Either one silently degraded the app to
  // network-only reads, which is the failure this whole adapter exists to
  // avoid. Declared before the executor because the handlers registered inside
  // it close over the binding, not its value.
  let cached: Promise<IDBDatabase>;
  const attempt = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    let settled = false;
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => {
      // `onblocked` may already have rejected this promise, but the open
      // request still completes later. That connection is cached nowhere —
      // close it, or it lingers as a stray handle that blocks the next
      // deleteDatabase during an account-switch wipe.
      if (settled) {
        try { request.result.close(); } catch { /* ignore */ }
        return;
      }
      settled = true;
      // A version change from another tab closes this connection; drop the
      // cached promise so the next call reopens instead of using a dead handle.
      request.result.onversionchange = () => {
        try { request.result.close(); } catch { /* ignore */ }
        if (dbPromise === cached) dbPromise = null;
      };
      request.result.onclose = () => {
        if (dbPromise === cached) dbPromise = null;
      };
      resolve(request.result);
    };
    request.onerror = () => {
      settled = true;
      reject(request.error);
    };
    request.onblocked = () => {
      settled = true;
      reject(new Error("indexedDB open blocked"));
    };
  });

  cached = attempt.catch((err) => {
    // Retry-able: forget the rejection so a later call can try again.
    if (dbPromise === cached) dbPromise = null;
    retryAfter = Date.now() + RETRY_BACKOFF_MS;
    throw err;
  });
  dbPromise = cached;

  return dbPromise;
}

/**
 * Close the cached database connection.
 * Used during full reset to ensure IndexedDB can be deleted.
 */
function closeDB(): void {
  if (dbPromise) {
    try {
      dbPromise.then((db) => {
        try { db.close(); } catch {}
      }).catch(() => {});
    } catch {}
    dbPromise = null;
  }
}

/**
 * Gun hands this hook two different `msg.put` shapes, and treating them as one
 * is what silently disabled local storage entirely:
 *
 *   graph form — `{ <soul>: { _: {...}, field: value } }`
 *   delta form — `{ '#': soul, '.': field, ':': value, '>': state }`
 *
 * The delta form is what a chain write (`gun.get(a).get(b).put(v)`) emits, so
 * it accounts for nearly every local write. Reading it as a graph yields the
 * keys `#`, `.`, `:` and `>` instead of souls, and the only one that survives
 * an `is object` check is `:` — which is why the store held a single entry
 * literally keyed `":"` while the real graph was never persisted at all.
 *
 * Returns graph form either way, so the writer below has one shape to handle.
 */
function asGraph(put: any): Record<string, any> | null {
  if (!put || typeof put !== 'object') return null;

  const soul = put['#'];
  const field = put['.'];
  if (typeof soul === 'string' && typeof field === 'string') {
    return {
      [soul]: {
        _: { '#': soul, '>': { [field]: put['>'] } },
        [field]: put[':'],
      },
    };
  }

  return put as Record<string, any>;
}

/** Merge one node into whatever is already stored under its soul. */
function mergeNode(existing: any, node: any): any {
  if (!existing || typeof existing !== 'object') return node;

  const updated: any = { ...existing, ...node };
  if (existing._ && node._) {
    // Gun's HAM state lives in `_['>']` PER FIELD. A partial put only carries
    // state for the fields it touches, so replacing the whole `>` map would
    // drop the state vectors of untouched fields — after a reload Gun could
    // then resurrect stale values or discard offline edits. Deep-merge `>`
    // and keep the soul.
    updated._ = {
      ...existing._,
      ...node._,
      '>': { ...(existing._ as any)?.['>'], ...(node._ as any)?.['>'] },
    };
    const soulRef = (node._ as any)?.['#'] ?? (existing._ as any)?.['#'];
    if (soulRef !== undefined) (updated._ as any)['#'] = soulRef;
  }
  return updated;
}

// Gun Plugin Implementation
(Gun as any).on('opt', function(this: any, ctx: any) {
  this.to.next(ctx);
  if (ctx.once) return;
  // 'opt' is GLOBAL: every Gun instance created in this process passes through
  // here. Only the instance that asked for this adapter (gunClient sets
  // `gunLayerIDB: true`) gets it — otherwise test-local instances silently shared
  // the app's IDB store and there was no way to run two isolated layers.
  if (!ctx.opt?.gunLayerIDB) return;

  // GET: Retrieve data from IndexedDB
  ctx.on('get', async function(this: any, msg: any) {
    this.to.next(msg);
    const get = msg.get;
    if (!get) return;
    const key = get['#'];
    if (!key) return;
    if (idbUnavailable || suspended) return;

    try {
      const db = await getDB();
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(key);
      
      req.onsuccess = () => {
        const data = req.result;
        if (!data) return;
        // Tell Gun we found the data locally
        ctx.on('in', {
          '@': msg['#'],
          put: { [key]: data },
          err: null
        });
      };
      // A failed read must be visible, not silent nothing: the outer catch
      // only covers getDB(), so without these a request- or transaction-level
      // error looked identical to a local miss.
      req.onerror = () => {
        console.error("[IDB Adapter] GET request error:", req.error);
      };
      tx.onerror = () => {
        if (tx.error) console.error("[IDB Adapter] GET tx error:", tx.error);
      };
    } catch (e) {
      // A store that is structurally absent (no IndexedDB in this environment)
      // or deliberately closed is not an error — local caching is an
      // optimisation. Only a genuine failure is worth reporting; the first read
      // in a non-browser environment was logging a stack trace on every run.
      if (!idbUnavailable && !suspended) console.error("[IDB Adapter] GET Error:", e);
    }
  });
  
  // PUT: Save data to IndexedDB
  ctx.on('put', async function(this: any, msg: any) {
    this.to.next(msg);
    const put = msg.put;
    if (!put) return;
    if (idbUnavailable || suspended) return;

    try {
      const graph = asGraph(put);
      if (!graph) return;

      const souls = Object.keys(graph).filter((soul) => {
        const node = graph[soul];
        return soul && soul !== '_' && node && typeof node === 'object';
      });
      if (souls.length === 0) return;

      const db = await getDB();

      // One transaction, and every request issued from within the previous
      // request's callback. Awaiting between souls let the transaction go
      // inactive, so a multi-soul put threw partway through and the remaining
      // souls were silently dropped along with the ack.
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);

        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);

        for (const soul of souls) {
          const getReq = store.get(soul);
          getReq.onsuccess = () => {
            store.put(mergeNode(getReq.result, graph[soul]), soul);
          };
        }
      });

      // A wipe may have started while this transaction was in flight; the
      // store it wrote to is being deleted, so do not claim local durability.
      if (suspended) return;

      // Acknowledge the write. Without this, Gun never acks a put while
      // offline, so every gunPutWithTimeout reports failure even though the data is
      // durably stored locally.
      //
      // STAMPED as local (see gunAck): this ack proves the write reached THIS
      // DEVICE's store and nothing more. Unstamped, it was indistinguishable
      // from a relay's ack, so a write the relay rejects — an unsigned put into
      // another user's `~<pub>` graph, say — resolved as a success.
      if (msg['#']) {
        ctx.on('in', { '@': msg['#'], ok: 1, err: null, [LOCAL_ACK_FLAG]: 1 });
      }
    } catch (e) {
      // Never err-ack: the local cache failing must not fail the write for the
      // caller, or a browser with storage blocked can't write anything at all.
      if (!idbUnavailable && !suspended) console.error("[IDB Adapter] PUT Error:", e);
    }
  });
});

/**
 * Exposed for tests: the message-shape handling that decides what gets cached,
 * plus the connection cache — whether a failed or closed connection is retried
 * is only observable through `getDB`, and getting it wrong silently degrades the
 * app to network-only reads for the rest of the session.
 */
export const __testing = {
  asGraph,
  mergeNode,
  isSuspended: () => suspended,
  getDB,
  /** Drop all cached connection state so a test starts from a cold module. */
  resetConnectionCache: () => {
    dbPromise = null;
    idbUnavailable = false;
    retryAfter = 0;
  },
};

export { DB_NAME, closeDB };
export default {};